/**
 * Cross-element stagger for multi-element preset application: pure delay
 * distribution, baked into each clip's `delay` at apply time so the result
 * is transparent in the timeline UI and undoable as one scene update.
 */
import type { ExcalidrawElement } from "@excalidraw/element/types";

export type StaggerFrom =
  | "first"
  | "last"
  | "center"
  | "random"
  | "left"
  | "top";

export const STAGGER_FROM_OPTIONS: readonly StaggerFrom[] = [
  "first",
  "last",
  "center",
  "random",
  "left",
  "top",
];

/** Deterministic integer hash → [0, 1) — same shape as textEffects' prand,
 * duplicated locally (packages must not depend on app code). */
const prand01 = (a: number, b: number) => {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = ((h ^ (h >> 13)) * 1274126177) | 0;
  return ((h ^ (h >> 16)) >>> 0) / 4294967296;
};

/** Delay offset per element id, in scene order. `random` is a deterministic
 * permutation of the `first` ramp for a given seed. */
export const computeStaggerDelays = (
  elements: readonly ExcalidrawElement[],
  opts: { eachMs: number; from: StaggerFrom; seed?: number },
): Map<string, number> => {
  const { eachMs, from, seed = 0 } = opts;
  const n = elements.length;
  const delays = new Map<string, number>();
  if (n === 0) {
    return delays;
  }
  // rank per element index
  let ranks: number[];
  switch (from) {
    case "last":
      ranks = elements.map((_, i) => n - 1 - i);
      break;
    case "center": {
      const mid = (n - 1) / 2;
      // distance from center, compacted to consecutive ranks so the ramp
      // has no gaps (0,0,1,1,… for even counts)
      const dist = elements.map((_, i) => Math.abs(i - mid));
      const sorted = [...new Set(dist)].sort((a, b) => a - b);
      ranks = dist.map((d) => sorted.indexOf(d));
      break;
    }
    case "random": {
      // Fisher-Yates over indices, driven by the seeded hash
      const order = elements.map((_, i) => i);
      for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(prand01(seed, i) * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      ranks = new Array(n);
      order.forEach((elIndex, rank) => {
        ranks[elIndex] = rank;
      });
      break;
    }
    // spatial ramps (anime.js grid-stagger equivalent for a free canvas):
    // rank by where the element sits, not by scene z-order
    case "left":
    case "top": {
      const centers = elements.map((el) =>
        from === "left"
          ? el.x + (el.width ?? 0) / 2
          : el.y + (el.height ?? 0) / 2,
      );
      const order = elements
        .map((_, i) => i)
        .sort((a, b) => centers[a] - centers[b]);
      ranks = new Array(n);
      order.forEach((elIndex, rank) => {
        ranks[elIndex] = rank;
      });
      break;
    }
    default:
      ranks = elements.map((_, i) => i);
  }
  elements.forEach((el, i) => {
    delays.set(el.id, Math.max(0, eachMs) * ranks[i]);
  });
  return delays;
};
